import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FiArrowRight, FiMail, FiX } from "react-icons/fi"
import AppLayout from "../layout/AppLayout"
import { announceMessagesChanged } from "../hooks/useUnreadMessages"
import {
  deleteMessage,
  getMessages,
  InboxMessage,
  markAllMessagesRead,
  markMessageRead
} from "../services/messageService"
import { formatApiError } from "../utils/formatApiError"

function formatDate(value: string) {
  return new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
}

export default function MessagesPage() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<InboxMessage[] | null>(null)
  const [error, setError] = useState("")
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await getMessages()
      setMessages(result?.messages ?? [])
    } catch (loadError) {
      setError(formatApiError(loadError, "The messages could not be loaded."))
      setMessages([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function open(message: InboxMessage) {
    if (!message.read) {
      setMessages((current) =>
        (current ?? []).map((entry) => (entry.id === message.id ? { ...entry, read: true } : entry))
      )
      try {
        await markMessageRead(message.id)
        announceMessagesChanged()
      } catch {
        // Reading is not blocked by a failed mark
      }
    }

    if (message.link) navigate(message.link)
  }

  async function readAll() {
    try {
      await markAllMessagesRead()
      setMessages((current) => (current ?? []).map((entry) => ({ ...entry, read: true })))
      announceMessagesChanged()
    } catch (readError) {
      setError(formatApiError(readError, "The messages could not be marked as read."))
    }
  }

  async function remove(message: InboxMessage) {
    if (deletingId) return
    setDeletingId(message.id)
    setError("")
    try {
      await deleteMessage(message.id)
      setMessages(current => (current ?? []).filter(entry => entry.id !== message.id))
      announceMessagesChanged()
    } catch (deleteError) {
      setError(formatApiError(deleteError, "The notification could not be deleted. Please try again."))
    } finally {
      setDeletingId(null)
    }
  }

  const hasUnread = (messages ?? []).some((message) => !message.read)

  return (
    <AppLayout title="Messages">
      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {messages === null ? (
        <p className="text-sm text-secondary4">Loading messages...</p>
      ) : messages.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-secondary5 bg-white px-6 py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-dantzGray text-dantzBlue">
            <FiMail size={24} />
          </div>
          <h2 className="mt-4 font-heading text-lg text-dantzBlue">No messages yet</h2>
          <p className="mt-1 max-w-sm text-sm text-secondary4">
            News about your applications will appear here.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-secondary5 bg-white">
          <div className="flex items-center justify-between border-b border-secondary5 px-5 py-3">
            <h2 className="font-heading text-base text-dantzBlue">Inbox</h2>
            <button
              type="button"
              disabled={!hasUnread}
              onClick={readAll}
              className="text-sm text-dantzBlue underline-offset-2 hover:underline disabled:cursor-default disabled:text-secondary4 disabled:no-underline"
            >
              Mark all as read
            </button>
          </div>

          <ul>
            {messages.map((message) => (
              <li key={message.id} className={`flex items-start gap-3 border-b border-secondary5 px-5 py-4 last:border-b-0 ${message.read ? "" : "bg-appTile"}`}>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm text-dantzBlue ${message.read ? "" : "font-semibold"}`}>
                    {message.title}
                  </p>
                  {message.body && <p className="mt-0.5 text-sm text-gray-600">{message.body}</p>}
                  <p className="mt-1 text-xs text-secondary4">{formatDate(message.createdAt)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => open(message)}
                    disabled={deletingId === message.id || (!message.link && message.read)}
                    aria-label={`View notification: ${message.title}`}
                    title={message.link ? "View application" : "Mark as read"}
                    className="rounded-md p-2 text-dantzBlue transition-colors hover:bg-dantzGray focus-visible:outline focus-visible:outline-2 focus-visible:outline-dantzBlue disabled:opacity-40"
                  >
                    <FiArrowRight size={18} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(message)}
                    disabled={deletingId !== null}
                    aria-label={`Delete notification: ${message.title}`}
                    title="Delete notification"
                    className="rounded-md p-2 text-secondary4 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 disabled:opacity-40"
                  >
                    <FiX size={18} aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AppLayout>
  )
}
